import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { editMode } from "../../../atoms/profileScreenEdit";
import { QUERY_KEYS } from "../../../constants/queryKeys";
import { EngineEarsError } from "../../../store/actions/errorStore";
import { ReleaseType } from "../../../store/models/release";
import { ArtistReleaseResponse, fetchArtistReleases } from "../releaseAPI";

interface UseFetchArtistReleasesQueryProps {
  artistUserId: number;
  releaseType: ReleaseType;
  page: number;
  isSubmitted?: boolean;
  isEditModeOverride?: boolean;
  enabled?: boolean;
}

export const useFetchArtistReleasesQuery = ({
  artistUserId,
  releaseType,
  page,
  isSubmitted = false, // Whether to filter releases by submitted (agreed to terms)
  enabled = true,
  isEditModeOverride,
}: UseFetchArtistReleasesQueryProps) => {
  const queryClient = useQueryClient();
  const isEditModeAtomValue = useAtomValue(editMode);
  // TODO: refactor this atom usage out entirely so appstate doesn't affect fetched data in other pages
  const isEditMode =
    isEditModeOverride !== undefined ? isEditModeOverride : isEditModeAtomValue;

  return useQuery({
    enabled: Boolean(artistUserId) && enabled,
    retry: 0,
    // Setting an arbitrary big stale time so when the user navigates quickly
    // the data isn't refetched
    // In addition, we don't need fresh data so frequent.
    staleTime: 10000,
    queryKey: [
      QUERY_KEYS.FETCH_ARTIST_RELEASES,
      {
        artistUserId,
        releaseType,
        page,
        isEditMode,
      },
    ],
    queryFn: async () => {
      const response = await fetchArtistReleases(
        artistUserId!,
        releaseType,
        page,
        isEditMode,
        isSubmitted,
      );
      if (!response.success) {
        throw response.resultJson as EngineEarsError;
      }
      const releasesResponse = response.resultJson as ArtistReleaseResponse;
      releasesResponse.data.forEach((release) => {
        queryClient.setQueryData(
          [QUERY_KEYS.FETCH_ARTIST_RELEASE, release.id],
          release,
        );
      });
      return releasesResponse;
    },
    placeholderData: keepPreviousData,
  });
};
